import { Boundaries } from "@planzone/core";
import { id, calendarHolidays } from "@planzone/dto";
import { events } from "@planzone/events";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { createStateReducer } from "../../utils/createStateReducer";
import { resetState } from "../../utils/resetState";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(
  state: State,
  { resource }: Boundaries["retrievePersonalCalendarHolidays"]["request"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToPending(),
          calendarHolidays: { ...calendarHolidays },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToPending(),
          calendarHolidays: { ...calendarHolidays },
        },
      ];
}

function handleSuccess(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["retrievePersonalCalendarHolidays"]["success"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToSuccess(),
          calendarHolidays,
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToSuccess(),
          calendarHolidays,
        },
      ];
}

function handleFailure(
  state: State,
  { resource }: Boundaries["retrievePersonalCalendarHolidays"]["failure"],
): State {
  const index = state.findIndex((req) => req.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        {
          resource,
          request: setRequestStateToFailure(),
          calendarHolidays: { ...calendarHolidays },
        },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        {
          resource,
          request: setRequestStateToFailure(),
          calendarHolidays: { ...calendarHolidays },
        },
      ];
}

function handleAssignCalendarHolidaysToResourceRequest(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["assignPersonalCalendarHolidays"]["request"],
): State {
  const index = state.findIndex((res) => res.resource.uuid === resource.uuid);

  return index !== -1
    ? [
        ...state.slice(0, index),
        { resource, calendarHolidays, request: setRequestStateToPending() },
        ...state.slice(index + 1),
      ]
    : [
        ...state,
        { resource, calendarHolidays, request: setRequestStateToPending() },
      ];
}

function handleAssignCalendarHolidaysToResourceSuccess(
  state: State,
  {
    resource,
    calendarHolidays,
  }: Boundaries["assignPersonalCalendarHolidays"]["success"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToSuccess() }
      : res,
  );
}

function handleAssignCalendarToHolidaysFailure(
  state: State,
  { resource }: Boundaries["assignPersonalCalendarHolidays"]["failure"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToFailure() }
      : res,
  );
}

function handleUnAssignPersonalCalendarHolidaysuccess(
  state: State,
  resource: Boundaries["unAssignPersonalCalendarHolidays"]["success"],
): State {
  return state.map((res) =>
    res.resource.uuid === resource.uuid
      ? { resource, calendarHolidays, request: setRequestStateToSuccess() }
      : res,
  );
}

const reduceState = resetState(
  initialState,
  createStateReducer(initialState, {
    [events.retrievePersonalCalendarHolidaysRequested]: handleRequest,
    [events.retrievePersonalCalendarHolidaysSucceeded]: handleSuccess,
    [events.retrievePersonalCalendarHolidaysFailed]: handleFailure,
    [events.assignPersonalCalendarHolidaysSucceeded]:
      handleAssignCalendarHolidaysToResourceSuccess,
    [events.assignPersonalCalendarHolidaysFailed]:
      handleAssignCalendarToHolidaysFailure,
    [events.assignPersonalCalendarHolidaysRequested]:
      handleAssignCalendarHolidaysToResourceRequest,
    [events.unAssignPersonalCalendarHolidaysSucceeded]:
      handleUnAssignPersonalCalendarHolidaysuccess,
  }),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
